import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

export type NetworkNode = d3.SimulationNodeDatum & {
  id: string;
  [key: string]: any;
};

export type NetworkLink = d3.SimulationLinkDatum<NetworkNode> & {
  source: string | NetworkNode;
  target: string | NetworkNode;
  [key: string]: any;
};

export interface NetworkGraphProps {
  nodes: NetworkNode[];
  links: NetworkLink[];
  width?: number | string;
  height?: number | string;
  nodeRadius?: (node: NetworkNode) => number;
  nodeColor?: (node: NetworkNode) => string;
  nodeLabel?: (node: NetworkNode) => string;
  nodeTooltip?: (node: NetworkNode) => string;
  linkStroke?: (link: NetworkLink) => string;
  linkStrokeWidth?: (link: NetworkLink) => number;
  linkStrokeOpacity?: (link: NetworkLink) => number;
  linkCurved?: boolean;
  linkDistance?: (link: NetworkLink) => number;
  onNodeHover?: (node: NetworkNode | null) => void;
  enablePanZoom?: boolean;
  forceStrength?: {
    charge?: number;
    link?: number;
    center?: [number, number];
    collision?: number;
  };
}

export default function NetworkGraph({
  nodes,
  links,
  width = '100%',
  height = 600,
  nodeRadius = (d) => 15,
  nodeColor = (d) => '#2196f3',
  nodeLabel = (d) => (d.title || d.id || '').toString(),
  nodeTooltip = (d) => (d.title || d.id || '').toString(),
  linkStroke = (d) => '#9e9e9e',
  linkStrokeWidth = (d) => 1.5,
  linkStrokeOpacity = (d) => 0.5,
  linkCurved = false,
  linkDistance = (d) => 80,
  onNodeHover,
  enablePanZoom = true,
  forceStrength = {
    charge: -300,
    link: 0.8,
    collision: 28,
  },
}: NetworkGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<NetworkNode, NetworkLink> | null>(null);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const container = containerRef.current;
    const containerWidth = typeof width === 'number' ? width : container.clientWidth || 800;
    const containerHeight = typeof height === 'number' ? height : (typeof height === 'string' ? parseInt(height) : 600);

    svg.attr('width', containerWidth).attr('height', containerHeight);

    // Create container group for zoom/pan
    const g = svg.append('g').attr('class', 'network-container');

    // Set up zoom behavior
    let transform = d3.zoomIdentity;
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        transform = event.transform;
        g.attr('transform', transform.toString());
      });

    if (enablePanZoom) {
      svg.call(zoom as any);
    }

    // Create force simulation
    const simulation = d3.forceSimulation<NetworkNode>(nodes)
      .force('link', d3.forceLink<NetworkNode, NetworkLink>(links)
        .id((d: any) => d.id)
        .distance((d: any) => linkDistance(d))
        .strength(forceStrength.link || 0.8))
      .force('charge', d3.forceManyBody().strength(forceStrength.charge || -300))
      .force('center', d3.forceCenter(
        (forceStrength.center?.[0] ?? containerWidth / 2),
        (forceStrength.center?.[1] ?? containerHeight / 2)
      ))
      .force('collision', d3.forceCollide().radius((d: any) => {
        const radius = nodeRadius(d);
        return (forceStrength.collision || radius) + 2;
      }));

    simulationRef.current = simulation;

    // Create links
    const link = g.append('g')
      .attr('class', 'links')
      .selectAll(linkCurved ? 'path' : 'line')
      .data(links)
      .enter()
      .append(linkCurved ? 'path' : 'line')
      .attr('fill', 'none')
      .attr('stroke', (d: any) => linkStroke(d))
      .attr('stroke-width', (d: any) => linkStrokeWidth(d))
      .attr('stroke-opacity', (d: any) => linkStrokeOpacity(d));

    // Create nodes
    const node = g.append('g')
      .attr('class', 'nodes')
      .selectAll('circle')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('r', (d: any) => nodeRadius(d))
      .attr('fill', (d: any) => nodeColor(d))
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'grab')
      .call(
        d3.drag<SVGCircleElement, NetworkNode>()
          .on('start', dragstarted)
          .on('drag', dragged)
          .on('end', dragended)
      );

    // Create labels
    const labels = g.append('g')
      .attr('class', 'labels')
      .selectAll('text')
      .data(nodes)
      .enter()
      .append('text')
      .text((d: any) => {
        const label = nodeLabel(d);
        return label.length > 20 ? label.substring(0, 20) + '...' : label;
      })
      .attr('font-size', '10px')
      .attr('text-anchor', 'middle')
      .attr('dy', (d: any) => nodeRadius(d) + 12)
      .attr('fill', '#333');

    // Add tooltips
    node.append('title').text((d: any) => nodeTooltip(d));

    // // Hover effects
    // let hoveredNode: NetworkNode | null = null;
    // node.on('mouseover', function (event, n: any) {
    //   hoveredNode = n;
    //   if (onNodeHover) {
    //     onNodeHover(n);
    //   }

    //   // Find connected nodes
    //   const connected = new Set<string>();
    //   links.forEach((l: any) => {
    //     const sid = typeof l.source === 'string' ? l.source : (l.source as NetworkNode).id;
    //     const tid = typeof l.target === 'string' ? l.target : (l.target as NetworkNode).id;
    //     if (sid === n.id) connected.add(tid);
    //     if (tid === n.id) connected.add(sid);
    //   });

    //   // Highlight connected nodes and links
    //   link.attr('stroke-opacity', (l: any) => {
    //     const sid = typeof l.source === 'string' ? l.source : (l.source as NetworkNode).id;
    //     const tid = typeof l.target === 'string' ? l.target : (l.target as NetworkNode).id;
    //     return (sid === n.id || tid === n.id) ? 0.9 : 0.06;
    //   });
    //   node.attr('opacity', (d: any) => (d.id === n.id || connected.has(d.id)) ? 1 : 0.25);
    //   labels.attr('opacity', (d: any) => (d.id === n.id || connected.has(d.id)) ? 1 : 0.15);
    // }).on('mouseout', function () {
    //   hoveredNode = null;
    //   if (onNodeHover) {
    //     onNodeHover(null);
    //   }

    //   link.attr('stroke-opacity', (d: any) => linkStrokeOpacity(d));
    //   node.attr('opacity', 1);
    //   labels.attr('opacity', 1);
    // });

    // Update positions on simulation tick
    simulation.on('tick', () => {
      if (linkCurved) {
        link.attr('d', (d: any) => {
          const sx = typeof d.source === 'string' ? 0 : (d.source as NetworkNode).x!;
          const sy = typeof d.source === 'string' ? 0 : (d.source as NetworkNode).y!;
          const tx = typeof d.target === 'string' ? 0 : (d.target as NetworkNode).x!;
          const ty = typeof d.target === 'string' ? 0 : (d.target as NetworkNode).y!;
          const mx = (sx + tx) / 2;
          const my = (sy + ty) / 2;
          const dx = tx - sx;
          const dy = ty - sy;
          const norm = Math.sqrt(dx * dx + dy * dy) || 1;
          const curve = 24;
          const cx = mx - (dy / norm) * curve;
          const cy = my + (dx / norm) * curve;
          return `M ${sx},${sy} Q ${cx},${cy} ${tx},${ty}`;
        });
      } else {
        link
          .attr('x1', (d: any) => typeof d.source === 'string' ? 0 : (d.source as NetworkNode).x!)
          .attr('y1', (d: any) => typeof d.source === 'string' ? 0 : (d.source as NetworkNode).y!)
          .attr('x2', (d: any) => typeof d.target === 'string' ? 0 : (d.target as NetworkNode).x!)
          .attr('y2', (d: any) => typeof d.target === 'string' ? 0 : (d.target as NetworkNode).y!);
      }

      node
        .attr('cx', (d: any) => d.x!)
        .attr('cy', (d: any) => d.y!);

      labels
        .attr('x', (d: any) => d.x!)
        .attr('y', (d: any) => d.y!);
    });

    function dragstarted(event: d3.D3DragEvent<SVGCircleElement, NetworkNode, NetworkNode>) {
      if (!event.active && simulation) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event: d3.D3DragEvent<SVGCircleElement, NetworkNode, NetworkNode>) {
      // Transform coordinates to account for zoom
      const point = d3.pointer(event, svg.node());
      const inverted = transform.invert(point);
      event.subject.fx = inverted[0];
      event.subject.fy = inverted[1];
    }

    function dragended(event: d3.D3DragEvent<SVGCircleElement, NetworkNode, NetworkNode>) {
      if (!event.active && simulation) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    // Cleanup
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };
  }, [nodes, links, width, height, nodeRadius, nodeColor, nodeLabel, nodeTooltip, 
      linkStroke, linkStrokeWidth, linkStrokeOpacity, linkCurved, linkDistance, 
      onNodeHover, enablePanZoom, forceStrength]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%', border: '1px solid #e0e0e0' }} />
    </div>
  );
}
